import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, logout, LOGO_URL } from "../api";
import i18n from "../i18n";
import { toast } from "sonner";

const LANGS = [["it","IT"],["en","EN"],["de","DE"],["fr","FR"]];

export default function LegalConsent() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next");
  const safeNext = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/feed";
  const [lang, setLang] = useState((localStorage.getItem("lang") || i18n.language || "it").slice(0,2));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);   // [{doc_type, version, content, content_hash}]
  const [checked, setChecked] = useState({});  // {terms:bool, privacy:bool}
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState({});      // {terms:bool, privacy:bool} lettura espansa

  const load = useCallback(async (lg) => {
    setLoading(true); setError(""); setChecked({}); setOpen({});
    try {
      const { data: pend } = await api.get("/legal/pending");
      const pending = pend.pending || [];
      if (!pending.length) { nav(safeNext, { replace: true }); return; }
      const built = [];
      for (const p of pending) {
        const { data: docs } = await api.get(`/legal/versions?doc_type=${p.doc_type}&lang=${lg}`);
        const doc = docs.find(d => d.version === p.version) || docs[0];
        if (doc) built.push({ doc_type: p.doc_type, version: doc.version, content: doc.content, content_hash: doc.content_hash });
      }
      setItems(built);
    } catch (e) {
      setError(t("legalc.error"));
    } finally { setLoading(false); }
  }, [nav, safeNext, t]);

  useEffect(() => { load(lang); }, [lang, load]);

  const changeLang = (lg) => {
    localStorage.setItem("lang", lg); i18n.changeLanguage(lg); setLang(lg);
  };

  const allChecked = items.length > 0 && items.every(it => checked[it.doc_type]);

  const submit = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    const failed = [];
    for (const it of items) {
      try {
        await api.post("/legal/accept", { doc_type: it.doc_type, version: it.version, lang, content_hash: it.content_hash });
      } catch (e) { failed.push(it.doc_type); }
    }
    try {
      const { data: pend } = await api.get("/legal/pending");
      const remaining = pend.pending || [];
      if (!remaining.length) { toast.success(t("legalc.all_done")); nav(safeNext, { replace: true }); return; }
      // Successo parziale: mantieni SOLO ciò che resta pendente, mostra quale riprovare.
      const rebuilt = [];
      for (const p of remaining) {
        const cur = items.find(i => i.doc_type === p.doc_type);
        if (cur) rebuilt.push(cur);
      }
      setItems(rebuilt);
      setChecked({});
      setError(t("legalc.partial_error", { list: remaining.map(p => t(`legalc.${p.doc_type}_name`)).join(", ") }));
    } catch (e) {
      setError(t("legalc.error"));
    } finally { setSubmitting(false); }
  };

  const label = (dt) => dt === "terms" ? t("legalc.accept_terms") : t("legalc.ack_privacy");
  const name = (dt) => dt === "terms" ? t("legalc.terms_name") : t("legalc.privacy_name");

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="legal-consent-page">
      <header className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-slate-200 bg-white">
        <img src={LOGO_URL} alt="YouPAM" className="h-8" />
        <div className="flex gap-1" role="group" aria-label={t("legalc.lang_label")}>
          {LANGS.map(([code, txt]) => (
            <button key={code} onClick={() => changeLang(code)}
              aria-pressed={lang === code}
              data-testid={`legal-lang-${code}`}
              className={`px-2.5 py-1 rounded text-sm font-medium transition-colors ${lang === code ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {txt}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="legal-consent-title">{t("legalc.title")}</h1>
        <p className="mt-2 text-sm text-slate-600">{t("legalc.subtitle")}</p>

        {loading && <div className="mt-8 text-slate-500" data-testid="legal-consent-loading">{t("legalc.loading")}</div>}

        {!loading && error && (
          <div role="alert" data-testid="legal-consent-error" className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-6 space-y-5">
            {items.map((it) => (
              <section key={it.doc_type} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5" data-testid={`legal-doc-${it.doc_type}`}>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-slate-900">{name(it.doc_type)}</h2>
                  <button onClick={() => setOpen(o => ({ ...o, [it.doc_type]: !o[it.doc_type] }))}
                    className="text-sm text-blue-600 underline shrink-0"
                    aria-expanded={!!open[it.doc_type]}
                    data-testid={`legal-read-${it.doc_type}`}>
                    {open[it.doc_type] ? t("legalc.hide") : t("legalc.read")}
                  </button>
                </div>
                <div
                  className={`mt-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed border border-slate-100 rounded p-3 bg-slate-50 overflow-y-auto ${open[it.doc_type] ? "max-h-none" : "max-h-40"}`}
                  data-testid={`legal-content-${it.doc_type}`}>
                  {it.content}
                </div>
                <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                  <input type="checkbox"
                    className="mt-1 h-5 w-5 accent-slate-900"
                    checked={!!checked[it.doc_type]}
                    onChange={(e) => setChecked(c => ({ ...c, [it.doc_type]: e.target.checked }))}
                    data-testid={`legal-check-${it.doc_type}`} />
                  <span className="text-sm text-slate-800">{label(it.doc_type)}</span>
                </label>
              </section>
            ))}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button onClick={submit} disabled={!allChecked || submitting}
                className="yp-btn yp-btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="legal-continue-btn">
                {submitting ? t("legalc.saving") : t("legalc.continue")}
              </button>
              <button onClick={() => logout()}
                className="yp-btn flex-1 border border-slate-300 text-slate-700 hover:bg-slate-100"
                data-testid="legal-decline-btn">
                {t("legalc.decline_exit")}
              </button>
            </div>
            <p className="text-xs text-slate-400 pt-1">{t("legalc.draft_note")}</p>
          </div>
        )}
      </main>
    </div>
  );
}
